import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatOrgTime } from "@/shared/utils/date/org-time";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { ChevronLeft, ChevronRight, Pin, PinOff, X } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Badge } from "@/ui/components/ui/badge";
import { cn } from "@/ui/lib/utils";
import { useGetEventDetail } from "@/machines/api";
import { useGetEventById } from "@/machines/api/queries/events.queries.ts";
import type { MapEventRow } from "@/map/hooks/useLocationEvents";
import { labelConfidencePercent, type ImageLabelLike } from "@/shared/utils/confidence";
import { useMapPinStore } from "@/map/stores/useMapPinStore";

// Durations arrive as float seconds — cap at 2 decimals (Number() drops trailing zeros).
const fmtSeconds = (s: number) => formatNumber(Number(s.toFixed(2)));
const fmtDuration = (s: number) =>
  s >= 60
    ? `${formatNumber(Math.floor(s / 60))}m ${fmtSeconds(s % 60)}s`
    : `${fmtSeconds(s)}s`;

interface MapMachineEventCardProps {
  event: MapEventRow;
  /** Shift left of the right-edge so it doesn't overlap the docked video drawer. */
  shiftLeft?: boolean;
  /**
   * Poll the event detail at this interval while it has no images yet — live
   * events start frameless and their frames land seconds later. Polling stops
   * once images arrive (each response re-signs the URLs, which would reflash
   * the rendered image).
   */
  pollMs?: number;
  onClose: () => void;
}

/**
 * On-map floating card for a machine event — the events counterpart of the
 * alert `MapEventCard` (same top-right slot and shell). Resolves the event
 * detail for its images and shows each frame's label confidence.
 */
const MapMachineEventCard = ({ event, shiftLeft = false, pollMs, onClose }: MapMachineEventCardProps) => {
  const { t } = useTranslation("map");
  // Truck-cycle rows carry a cycle UUID the unified event-detail endpoint
  // doesn't serve, and truck-layer rows have no uuid at all — skip the fetch
  // (and the image block below) for both.
  const { data: detail } = useGetEventDetail(
    event.isTruckCycle || !event.uuid ? null : event.uuid,
    { pollMsWhileFrameless: pollMs ?? false },
  );

  // The unified detail only carries ML-labelled frames, so many events come
  // back imageless even though raw frames exist — fall back to the legacy
  // `/api/events/{id}/` response's signed `imageUrls` (no labels there). Also
  // the only image source for truck-layer rows, which have no uuid at all.
  const legacyId = /^\d+$/.test(event.id) ? event.id : "";
  const wantLegacyImages =
    !event.isTruckCycle &&
    (event.uuid ? detail != null && (detail.images?.length ?? 0) === 0 : true);
  const { data: legacyEvent } = useGetEventById(wantLegacyImages ? legacyId : "");

  const images = useMemo((): { url: string; labels?: ImageLabelLike[] }[] => {
    const labelled = detail?.images ?? [];
    if (labelled.length > 0) return labelled.map((im) => ({ url: im.signed_url, labels: im.labels }));
    return (legacyEvent?.data?.imageUrls ?? []).map((url) => ({ url }));
  }, [detail, legacyEvent]);
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    setImageIndex(0);
  }, [event.id]);

  // Layout lock (persisted, shared with the other map panels): freeze the card's
  // position (`shiftLeft`) at pin time so a docking video or other data change
  // can't move it. Live-follow may still swap `event` (subject flows); only the
  // position is frozen.
  const isPinned = useMapPinStore((s) => s.isPinned("eventCard"));
  const togglePin = useMapPinStore((s) => s.togglePin);
  const [frozenShiftLeft, setFrozenShiftLeft] = useState<boolean | null>(null);
  useEffect(() => {
    if (isPinned && frozenShiftLeft == null) setFrozenShiftLeft(shiftLeft);
    else if (!isPinned && frozenShiftLeft != null) setFrozenShiftLeft(null);
  }, [isPinned, shiftLeft, frozenShiftLeft]);
  const effectiveShiftLeft = frozenShiftLeft ?? shiftLeft;

  const safeIndex = images.length > 0 ? Math.min(imageIndex, images.length - 1) : 0;
  const currentImage = images[safeIndex] ?? null;
  const confidence = currentImage ? labelConfidencePercent(currentImage.labels) : null;

  return (
    <div
      className={cn(
        "absolute top-4 z-30 w-80 max-h-[calc(100%-2rem)] flex flex-col rounded-lg border bg-background shadow-2xl animate-in slide-in-from-right-2 duration-200",
        effectiveShiftLeft ? "right-[356px]" : "right-4",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-sidebar/50 rounded-t-lg p-3">
        <h3 className="text-sm font-semibold">{t("eventCard.title")}</h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className={cn("h-7 w-7", isPinned && "text-foreground")} onClick={() => togglePin("eventCard")} title={isPinned ? t("eventCard.unpin") : t("eventCard.pin")}>
            {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} title={t("eventCard.close")}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Image carousel — truck-cycle rows have no event detail, so no image block */}
        {!event.isTruckCycle && (
          <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black">
            {currentImage ? (
              <img src={currentImage.url} alt={event.state} className="h-full w-full object-contain" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-white/60">
                {t("eventCard.noImage")}
              </div>
            )}

            {confidence != null && (
              <div className="absolute bottom-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                {t("eventCard.confidence", { value: confidence })}
              </div>
            )}

            {images.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label={t("eventCard.previousImage")}
                  onClick={() => setImageIndex((i) => (i <= 0 ? images.length - 1 : i - 1))}
                  className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={t("eventCard.nextImage")}
                  onClick={() => setImageIndex((i) => (i >= images.length - 1 ? 0 : i + 1))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <div className="absolute bottom-1.5 right-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                  {safeIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        )}

        {/* State + productivity */}
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: event.color }} />
          <span className="text-sm font-medium">{event.state}</span>
          <Badge
            variant="outline"
            className={cn(
              "ml-auto text-[10px] h-5 px-1.5",
              event.productive ? "text-green-600" : "text-muted-foreground",
            )}
          >
            {event.productive ? t("eventCard.productive") : t("eventCard.idle")}
          </Badge>
        </div>

        {/* Date / Time */}
        <div>
          <p className="text-[10px] text-muted-foreground">{t("eventCard.dateTime")}</p>
          <p className="text-xs font-medium font-mono">
            {Number.isNaN(event.startTime.getTime())
              ? "—"
              : formatOrgTime(event.startTime, "MMM DD, YYYY HH:mm:ss")}{" "}
            · {fmtDuration(event.durationSeconds)}
          </p>
        </div>

        {/* Machine */}
        <div>
          <p className="text-[10px] text-muted-foreground">{t("eventCard.machine")}</p>
          <p className="text-xs font-medium">{event.machineName}</p>
        </div>
      </div>
    </div>
  );
};

export default MapMachineEventCard;
